import React, { useState, useEffect } from 'react'
import { Card, Table, Button, Tag, Modal, Form, Input, InputNumber, DatePicker, Space, message, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { RangePicker } = DatePicker

const FlashSaleManage = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [modalVisible, setModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async (page = 1) => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/admin/flashsale/list?page=${page}&limit=20`)
      const result = await res.json()
      if (result.success) {
        setData(result.data || [])
        setPagination(prev => ({ ...prev, total: result.total || 0, current: page }))
      }
    } catch (err) {
      message.error('加载失败')
    }
    setLoading(false)
  }

  const handleAdd = () => {
    setEditingItem(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    setEditingItem(record)
    form.setFieldsValue({
      ...record,
      dateRange: [record.start_time, record.end_time]
    })
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/admin/flashsale/${id}`, { method: 'DELETE' })
      const result = await res.json()
      if (result.success) {
        message.success('删除成功')
        loadData()
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const method = editingItem ? 'PUT' : 'POST'
      const url = editingItem 
        ? `${API_BASE}/api/admin/flashsale/${editingItem.id}` 
        : `${API_BASE}/api/admin/flashsale`

      const body = {
        ...values,
        start_time: values.dateRange[0],
        end_time: values.dateRange[1]
      }
      delete body.dateRange

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const result = await res.json()
      
      if (result.success) {
        message.success(editingItem ? '修改成功' : '创建成功')
        setModalVisible(false)
        loadData()
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    { title: '商品', dataIndex: 'product_name', width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: 12, color: '#999' }}>ID: {record.product_id}</div>
        </div>
      )
    },
    { title: '秒杀价', dataIndex: 'sale_price', width: 100,
      render: v => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>¥{v}</span>
    },
    { title: '原价', dataIndex: 'original_price', width: 100,
      render: v => <span style={{ textDecoration: 'line-through', color: '#999' }}>¥{v}</span>
    },
    { title: '折扣', dataIndex: 'discount', width: 80,
      render: v => v ? <Tag color="red">{v}折</Tag> : '-'
    },
    { title: '库存', dataIndex: 'stock', width: 80 },
    { title: '已售', dataIndex: 'sold_count', width: 80 },
    { title: '时间', dataIndex: 'start_time', width: 180,
      render: (text, record) => (
        <span>{text?.substring(0,16)} ~ {record.end_time?.substring(11,16)}</span>
      )
    },
    { title: '状态', dataIndex: 'status', width: 100,
      render: s => {
        const config = {
          active: { color: 'green', text: '进行中' },
          upcoming: { color: 'orange', text: '未开始' },
          ended: { color: 'default', text: '已结束' }
        }
        const { color, text } = config[s] || config.upcoming
        return <Tag color={color}>{text}</Tag>
      }
    },
    { title: '操作', width: 120, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>⏰ 限时折扣管理</h2>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>创建秒杀</Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: loadData
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingItem ? '编辑秒杀' : '创建秒杀'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="product_id" label="商品ID" rules={[{ required: true }]}>
            <Input placeholder="请输入商品ID" />
          </Form.Item>
          <Form.Item name="product_name" label="商品名称" rules={[{ required: true }]}>
            <Input placeholder="请输入商品名称" />
          </Form.Item>
          <Space>
            <Form.Item name="sale_price" label="秒杀价" rules={[{ required: true }]}>
              <InputNumber min={0} precision={2} prefix="¥" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="original_price" label="原价" rules={[{ required: true }]}>
              <InputNumber min={0} precision={2} prefix="¥" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="stock" label="库存" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: 100 }} />
            </Form.Item>
          </Space>
          <Form.Item name="dateRange" label="活动时间" rules={[{ required: true }]}>
            <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default FlashSaleManage
